import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import {
  HotelApiError,
  type OnboardingInput,
  type PropertySettingsInput,
  type PropertySummary,
  type StayFilters,
  hotelApi,
} from './api';

export type HotelScope = {
  organizationId: number;
  property: PropertySummary;
};

export const HotelScopeContext = createContext<HotelScope | null>(null);

export function useHotelScope(): HotelScope {
  const scope = useContext(HotelScopeContext);
  if (!scope) throw new Error('useHotelScope must be used inside /hotel');
  return scope;
}

const retry = (failureCount: number, error: Error) =>
  !(
    error instanceof HotelApiError &&
    error.status !== null &&
    error.status >= 400 &&
    error.status < 500
  ) && failureCount < 2;

export const hotelKeys = {
  all: ['hotel'] as const,
  properties: (org: number) => ['hotel', org, 'properties'] as const,
  property: (org: number, id: number) =>
    ['hotel', org, 'property', id] as const,
  settings: (org: number, id: number) =>
    [...hotelKeys.property(org, id), 'settings'] as const,
  onboarding: (org: number, id: number) =>
    [...hotelKeys.property(org, id), 'onboarding'] as const,
  stays: (org: number, id: number, filters: StayFilters) =>
    [...hotelKeys.property(org, id), 'stays', filters] as const,
  stay: (org: number, id: number, stayRef: string) =>
    [...hotelKeys.property(org, id), 'stay', stayRef] as const,
};

export function useProperties(organizationId: number | null) {
  return useQuery({
    queryKey: hotelKeys.properties(organizationId ?? 0),
    queryFn: () => hotelApi.properties(organizationId!),
    enabled: organizationId !== null,
    staleTime: 60_000,
    retry,
    // Both shells, the switcher and the hotel gate all read this list, and
    // its answer decides which of them mount. A failed list (a restaurant
    // group's 404) must stay failed when a new reader mounts: retrying it
    // would make it pending again and unmount the reader that asked. The
    // hotel gate's Retry button still refetches.
    retryOnMount: false,
  });
}

/**
 * The groups a platform admin may open. Only the group picker and the
 * sidebar's group row ask; `enabled: false` keeps it quiet for everyone else.
 */
export function useStoreGroups({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...hotelKeys.all, 'store-groups'],
    queryFn: hotelApi.storeGroups,
    enabled,
    staleTime: 5 * 60_000,
    retry,
  });
}

/**
 * A stays page for a property named explicitly, for the hotel sidebar, which
 * sits outside `HotelScopeContext`. Same key as `useStays` with the same
 * filters, so the Stays badge and the Overview's "On hold" tile share one
 * request. Asks nothing until both ids are known.
 */
export function useStaysCount(
  organizationId: number | null,
  propertyId: number | null,
  filters: StayFilters,
) {
  return useQuery({
    queryKey: hotelKeys.stays(organizationId ?? 0, propertyId ?? 0, filters),
    queryFn: () => hotelApi.stays(organizationId!, propertyId!, filters),
    enabled: organizationId !== null && propertyId !== null,
    staleTime: 30_000,
    retry,
  });
}

export function useStays(filters: StayFilters) {
  const { organizationId: org, property } = useHotelScope();
  return useQuery({
    queryKey: hotelKeys.stays(org, property.id, filters),
    queryFn: () => hotelApi.stays(org, property.id, filters),
    // Keep rows while paging or filtering, never across properties: another
    // property's stays would show, and link, under this one.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[3] === property.id
        ? keepPreviousData(previous)
        : undefined,
    staleTime: 30_000,
    retry,
  });
}

export function useStay(stayRef: string) {
  const { organizationId: org, property } = useHotelScope();
  return useQuery({
    queryKey: hotelKeys.stay(org, property.id, stayRef),
    queryFn: () => hotelApi.stay(org, property.id, stayRef),
    staleTime: 30_000,
    retry,
  });
}

export function useSettings() {
  const { organizationId: org, property } = useHotelScope();
  return useQuery({
    queryKey: hotelKeys.settings(org, property.id),
    queryFn: () => hotelApi.settings(org, property.id),
    retry,
  });
}

export function useOnboarding() {
  const { organizationId: org, property } = useHotelScope();
  return useQuery({
    queryKey: hotelKeys.onboarding(org, property.id),
    queryFn: () => hotelApi.onboarding(org, property.id),
    retry,
  });
}

/** Settings and onboarding both change the blockers and the voice switch. */
function useRefreshProperty() {
  const client = useQueryClient();
  const { organizationId: org, property } = useHotelScope();
  return () => {
    void client.invalidateQueries({ queryKey: hotelKeys.properties(org) });
    void client.invalidateQueries({
      queryKey: hotelKeys.property(org, property.id),
    });
  };
}

export function useSaveSettings() {
  const { organizationId: org, property } = useHotelScope();
  const client = useQueryClient();
  const refresh = useRefreshProperty();
  return useMutation({
    mutationFn: (values: Partial<PropertySettingsInput>) =>
      hotelApi.saveSettings(org, property.id, values),
    onSuccess: (saved) => {
      client.setQueryData(hotelKeys.settings(org, property.id), saved);
      refresh();
    },
  });
}

export function useSaveOnboarding() {
  const { organizationId: org, property } = useHotelScope();
  const refresh = useRefreshProperty();
  return useMutation({
    mutationFn: (values: OnboardingInput) =>
      hotelApi.saveOnboarding(org, property.id, values),
    onSuccess: refresh,
    // The variables hold the access token; drop the mutation from the cache
    // (and the devtools) as soon as it settles and is reset.
    gcTime: 0,
  });
}
